// The Record's banners for the AE's side of handoffs (spec C6, C7.4; the Record board, kit `hob` and
// Banner): the AE's own banner while a handoff waits on them or is with them, and the AE's note the
// VA reads at the top of the Record after a send back, which they can dismiss.
import { useState } from 'react';
import type { BackFromAe, OpenHandoff } from '../auth/api';
import { personKey, readJson, writeJson } from '../shell/browser-store';
import { CheckIcon, FileIcon } from '../shell/icons';
import { Tip } from '../shell/Tip';
import { FollowUpBox } from './FollowUp';
import {
  actByChip,
  aeBannerTitle,
  backFromAeText,
  followUpText,
  reasonLong,
  reasonPillCls,
} from './ae-handoff-lines';

/**
 * The AE's banner under the Record header: the reason, who sent it and when, the Act by and the VA's
 * note. While it waits: Send back and Accept. Once accepted: Add note, Follow up on (its box opens in
 * the banner), Pass to another AE and Finish handoff (S15 batches 6 and 7).
 */
export function AeHandoffBanner({
  h,
  onAccept,
  onSendBack,
  onAddNote,
  onFollowUp,
  onFinish,
  onPass,
}: {
  h: OpenHandoff;
  onAccept: () => void;
  onSendBack: () => void;
  /** Takes the AE to the Notes card's box. */
  onAddNote: () => void;
  /** Follow up on: true once saved, else why not. */
  onFollowUp: (on: string | null) => Promise<true | string>;
  /** Finish handoff's dialog; null until the steps are read. */
  onFinish: (() => void) | null;
  /** Pass to another AE's dialog (S15 batch 7). */
  onPass: () => void;
}) {
  const now = new Date();
  const [followingUp, setFollowingUp] = useState(false);
  const { title, from } = aeBannerTitle(h);
  const act = actByChip(h, now, true);
  const follow = followUpText(h.followUpOn, now);
  const reason = reasonLong(h);
  return (
    <section className="hob" aria-label="Handoff">
      <div className="row g8 wrap">
        {reason && <span className={reasonPillCls(h)}>{reason}</span>}
        <span className="t14 w6 ink">{title}</span>
        <span className="t12 muted">{from}</span>
        <span className="ml-auto row g6">
          {follow && <span className="tag t-blue">{follow}</span>}
          {act && (
            <Tip text={act.tip} focusable>
              <span className={act.cls}>{act.text}</span>
            </Tip>
          )}
        </span>
      </div>
      {h.note && <blockquote className="vnote">{h.note}</blockquote>}
      {followingUp && (
        <FollowUpBox
          day={h.followUpOn ?? null}
          now={now}
          onCancel={() => setFollowingUp(false)}
          onSave={async (on) => {
            const result = await onFollowUp(on);
            if (result === true) setFollowingUp(false);
            return result;
          }}
        />
      )}
      {!followingUp && (
        <div className="row g8 wrap">
          {h.state === 'waiting' ? (
            <>
              <button type="button" className="btn" aria-haspopup="dialog" onClick={onSendBack}>
                Send back
              </button>
              <button type="button" className="btn btn-primary ml-auto" onClick={onAccept}>
                <CheckIcon className="ico s14" />
                Accept
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn" onClick={onAddNote}>
                Add note
              </button>
              <button type="button" className="btn" onClick={() => setFollowingUp(true)}>
                Follow up on
              </button>
              <button type="button" className="btn" aria-haspopup="dialog" onClick={onPass}>
                Pass to another AE
              </button>
              <span className="ml-auto" />
              <button
                type="button"
                className="btn btn-primary"
                aria-haspopup="dialog"
                disabled={!onFinish}
                onClick={() => onFinish?.()}
              >
                Finish handoff
              </button>
            </>
          )}
        </div>
      )}
    </section>
  );
}

const seenKey = (userId: string) => personKey('back-from-ae-seen', userId, 'record');
const isIds = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'string');

/**
 * The AE's note at the top of the Record after a send back or a return (the Record board): until the
 * person dismisses it, remembered in this browser per person and handoff.
 */
export function BackFromAeBanner({
  b,
  userId,
  step,
}: {
  b: BackFromAe;
  userId: string;
  /** Continue at's name on a return, from the steps as read. */
  step: string | null;
}) {
  const [seen, setSeen] = useState(() => readJson('local', seenKey(userId), [], isIds));
  if (seen.includes(b.handoffId)) return null;
  const dismiss = () => {
    // The newest few are enough: an old handoff never comes back as the latest one.
    const next = [b.handoffId, ...seen].slice(0, 50);
    setSeen(next);
    writeJson('local', seenKey(userId), next);
  };
  return (
    <div className="bnr info" role="status">
      <FileIcon />
      <span>{backFromAeText(b, new Date(), step)}</span>
      <button type="button" className="btn btn-sm ml-auto" onClick={dismiss}>
        Dismiss
      </button>
    </div>
  );
}
